'use client';

import type { List } from '@swishapp/api-client';
import { useQueries } from '@tanstack/react-query';
import { WISHLIST_QUERY_KEYS } from '../wishlist-constants';
import { WishlistProxyClient } from '../wishlist-proxy-client';
import type { WishlistError } from '../wishlist-types';
import { useWishlistLists } from './use-wishlist-lists';
import { useWishlistSessionToken } from './use-wishlist-session-token';

function formatWishlistError(error: WishlistError): string {
  return typeof error === 'object' && error.message
    ? error.message
    : String(error);
}

/**
 * Hook to fetch all wishlist lists with their detailed data using parallel queries
 */
export function useWishlistListsDetailed() {
  const {
    data: authToken,
    isLoading: tokenLoading,
    isError: tokenError,
  } = useWishlistSessionToken();
  const {
    data: lists = [],
    isLoading: listsLoading,
    isError: listsError,
  } = useWishlistLists();

  const detailQueries = useQueries({
    queries: lists.map((list: List) => ({
      queryKey: [WISHLIST_QUERY_KEYS.LIST, list.id, authToken],
      queryFn: async () => {
        if (!authToken) throw new Error('No auth token available');

        const client = new WishlistProxyClient(authToken);
        const result = await client.getList(list.id);

        if (result.error) {
          throw new Error(formatWishlistError(result.error));
        }

        return result.data;
      },
      enabled:
        !!authToken &&
        !tokenLoading &&
        !tokenError &&
        !listsLoading &&
        !listsError,
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Combine loading states
  const isLoading =
    tokenLoading || listsLoading || detailQueries.some((q) => q.isLoading);
  const isError =
    tokenError || listsError || detailQueries.some((q) => q.isError);

  // Filter out null/undefined results and ensure all queries are successful
  const detailedLists = detailQueries
    .map((q) => q.data)
    .filter((data): data is NonNullable<typeof data> => data != null);

  // Only return data when all queries are complete
  const allQueriesComplete =
    detailQueries.length > 0 && detailQueries.every((q) => q.isSuccess);

  return {
    data: allQueriesComplete ? detailedLists : null,
    isLoading,
    isError,
    error: detailQueries.find((q) => q.error)?.error || null,
  };
}
